import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { authStore } from '@/store/authStore'
import { papelPrincipal } from '@/components/layout/menu'
import ReclassificacaoProdutoCard from '@/components/reservas/ReclassificacaoProdutoCard'
import { curadoriaService } from '@/services/curadoriaService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { estruturacaoConsultorService } from '@/services/estruturacaoConsultorService'
import { diagnosticoService } from '@/services/diagnosticoService'
import { documentoService } from '@/services/documentoService'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import {
  ArquivoPreviewDialog,
  ChecklistComArquivos,
  useArquivoReserva,
} from '@/components/documentos/ChecklistComArquivos'
import { AlertaItensBloqueantes, CabecalhoVersaoChecklist } from '@/components/documentos/ItemChecklistInfo'
import { ValidarItemDialog } from '@/components/curadoria/ValidarItemDialog'
import { RecusarItemDialog } from '@/components/curadoria/RecusarItemDialog'
import { DevolverItemDialog } from '@/components/curadoria/DevolverItemDialog'
import { SolicitarDocumentoDialog } from '@/components/curadoria/SolicitarDocumentoDialog'
import { HistoricoDecisoesDialog } from '@/components/curadoria/HistoricoDecisoesDialog'
import { MotivoDialog } from '@/components/curadoria/MotivoDialog'
import {
  checklistReservaService,
  itensBloqueantesDoErro,
  mensagemErroChecklistReserva,
} from '@/services/checklistReservaService'
import type { ItemBloqueanteChecklist, SolicitacaoDocumentoReserva } from '@/types/checklist'
import { gruposComItens, rotuloDoGrupo, rotuloDoItem } from '@/utils/checklistReserva'
import {
  CuradoriaReserva,
  DecisaoCuradoria,
  DECISAO_CURADORIA_LABEL,
  STATUS_CURADORIA_COR,
  STATUS_CURADORIA_LABEL,
} from '@/types/curadoria'
import {
  CampoDiagnostico,
  ChecklistReserva,
  DiagnosticoReserva,
  ItemChecklistReserva,
  ReservaEstruturacao,
} from '@/types/estruturacao'
import { formularioService } from '@/services/formularioService'
import { EtapaFormulario, FormularioReserva, temFormulario } from '@/types/formulario'
import { RespostasFormularioLeitura } from '@/components/formulario/RespostasFormularioLeitura'
import { Documento } from '@/types/documento'
import {
  AVALIACAO_MANUAL_LABEL,
  AvaliacaoManual,
  RespostaElegibilidadeReserva,
} from '@/types/oportunidade'
import { CANAL_LABEL, CanalComercial, STATUS_LABEL } from '@/types/statusReserva'
import {
  formatCNPJ,
  formatCurrency,
  formatDate,
  formatDateTime,
} from '@/utils/formatters'

/**
 * Validação da reserva (back-office, 06/09) — a tela em que ADMIN/GERENTE/OPERADOR
 * conferem TUDO o que o consultor inseriu antes de aprovar: dados da reserva, dados
 * solicitados pelo produto (formulário dinâmico, CADASTRO e ESTRUTURACAO, pela versão
 * vinculada — inclusive a LEGADO dos antigos dados complementares), respostas do diagnóstico, checklist e os arquivos em si (visualizar
 * e baixar). A aprovação exige marcar "dados conferidos" e "documentos conferidos".
 *
 * Fontes (todas já existentes, no escopo do token — back-office é global):
 *  - `GET /v1/oportunidades/curadoria` → status derivado, duplicidades, checklist %;
 *  - `GET /v1/oportunidades/{id}` → a reserva com os campos da Onda 4;
 *  - `GET /v1/oportunidades/{id}/formulario?etapa=` → schema da versão + respostas;
 *  - `GET /v1/oportunidades/{id}/diagnostico` + campos do produto → respostas rotuladas;
 *  - `GET /v1/oportunidades/{id}/checklist` → status por item da versão presa (P3), com
 *    Validar/Recusar por item, histórico de decisões, "Solicitar documento" (Q70) e
 *    "Atualizar checklist para a versão vigente" (`checklistReservaService`);
 *  - `GET /v1/documentos?tipoEntidade=OPORTUNIDADE&entidadeReferencia={id}` + `/arquivo`.
 * A decisão é a mesma da fila (`POST /{id}/curadoria/decisao`): APROVAR → APROVADA_ORIGINEI
 * (libera o agendamento da reunião), SOLICITAR_COMPLEMENTO → COM_PENDENCIA, REPROVAR.
 */

const rotuloCanal = (canal: string | null | undefined): string =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : '—'

const nomeEmpresa = (r: { razaoSocial?: string | null; nomeFantasia?: string | null; cnpj?: string | null }) =>
  r.razaoSocial || r.nomeFantasia || (r.cnpj ? formatCNPJ(r.cnpj) : 'Empresa sem nome')

const valorDaResposta = (
  campo: CampoDiagnostico,
  diagnostico: DiagnosticoReserva | null
): string => {
  const r = diagnostico?.respostas.find((x) => x.campoId === campo.id)
  if (!r) return '—'
  if (r.valorTexto != null && r.valorTexto !== '') return r.valorTexto
  if (r.valorNumero != null) {
    return campo.tipo === 'MOEDA' ? formatCurrency(r.valorNumero) : String(r.valorNumero)
  }
  if (r.valorData) return formatDate(r.valorData)
  return '—'
}

/** Par rótulo/valor da grade de dados. `vazio` é o texto quando não há valor (padrão "—"). */
const Campo: FC<{ rotulo: string; valor?: string | number | null; largo?: boolean; vazio?: string }> = ({
  rotulo,
  valor,
  largo = false,
  vazio = '—',
}) => (
  <Box sx={{ gridColumn: largo ? '1 / -1' : undefined }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {rotulo}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
      {valor == null || valor === '' ? vazio : valor}
    </Typography>
  </Box>
)

/** Campo do formulário visível e em branco. */
const NAO_INFORMADO = 'Não informado pelo consultor'

/** Status da fila da curadoria: só neles o backend aceita devolver item como pendência. */
const STATUS_DEVOLVER = new Set<string>(['ENVIADA', 'EM_ANALISE_ORIGINEI', 'COM_PENDENCIA'])

/** Etapas do formulário do produto, na ordem da jornada, com o título do bloco. */
const ETAPAS_FORMULARIO: { etapa: EtapaFormulario; titulo: string }[] = [
  { etapa: 'CADASTRO', titulo: 'Dados solicitados pelo produto (cadastro)' },
  { etapa: 'ESTRUTURACAO', titulo: 'Dados do produto (estruturação)' },
]

/**
 * O bloco da etapa aparece? Só com formulário e já fixado na reserva (ou com resposta) —
 * a vigente apenas "oferecida" a uma reserva que ainda não chegou à etapa não é dado dela.
 * Sem nada = sem bloco.
 */
const mostrarFormulario = (f: FormularioReserva | null | undefined): f is FormularioReserva =>
  temFormulario(f) && !!f && (f.vinculada || (f.respostas?.length ?? 0) > 0)

/** "versão N" / "legado" (versão 0 = os 7 dados complementares da V39). */
const rotuloVersao = (f: FormularioReserva): string | null =>
  f.versao == null ? null : f.versao === 0 ? 'legado' : `versão ${f.versao}`

const ValidacaoReservaPage: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const papel = papelPrincipal(usuario?.permissoes)
  const backoffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'

  const [reserva, setReserva] = useState<ReservaEstruturacao | null>(null)
  const [naFila, setNaFila] = useState<CuradoriaReserva | null>(null)
  const [campos, setCampos] = useState<CampoDiagnostico[]>([])
  const [diagnostico, setDiagnostico] = useState<DiagnosticoReserva | null>(null)
  const [checklist, setChecklist] = useState<ChecklistReserva | null>(null)
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoDocumentoReserva[]>([])
  const [documentos, setDocumentos] = useState<Documento[]>([])
  // P3 — validação documental por item (back-office).
  const [validando, setValidando] = useState<ItemChecklistReserva | null>(null)
  const [recusando, setRecusando] = useState<ItemChecklistReserva | null>(null)
  const [devolvendo, setDevolvendo] = useState<ItemChecklistReserva | null>(null)
  const [historico, setHistorico] = useState<ItemChecklistReserva | null>(null)
  const [solicitarAberto, setSolicitarAberto] = useState(false)
  const [cancelando, setCancelando] = useState<SolicitacaoDocumentoReserva | null>(null)
  const [atualizarVersaoAberto, setAtualizarVersaoAberto] = useState(false)
  /** 422 com os itens do checklist que barraram a transição. */
  const [bloqueio, setBloqueio] = useState<{ mensagem: string; itens: ItemBloqueanteChecklist[] } | null>(null)
  // Respostas de elegibilidade gravadas na reserva (09/09). Texto livre é decidido AQUI,
  // pelo operador/admin — o motor não compara prosa com nada.
  const [respostasElegibilidade, setRespostasElegibilidade] = useState<RespostaElegibilidadeReserva[]>([])
  /** Formulário do produto por etapa (CADASTRO, ESTRUTURACAO) — leitura pela versão. */
  const [formularios, setFormularios] = useState<Partial<Record<EtapaFormulario, FormularioReserva | null>>>({})
  const [avaliandoPergunta, setAvaliandoPergunta] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  /** Falhas de complementos (diagnóstico, checklist, documentos) não derrubam a tela. */
  const [avisos, setAvisos] = useState<string[]>([])

  const [dadosConferidos, setDadosConferidos] = useState(false)
  const [docsConferidos, setDocsConferidos] = useState(false)
  const [decisao, setDecisao] = useState<DecisaoCuradoria>('APROVAR')
  const [motivo, setMotivo] = useState('')
  const [pendDescricao, setPendDescricao] = useState('')
  const [pendDocumento, setPendDocumento] = useState('')
  const [pendPrazo, setPendPrazo] = useState('')
  const [decidindo, setDecidindo] = useState(false)
  const [erroDecisao, setErroDecisao] = useState<string | null>(null)

  const [iniciando, setIniciando] = useState(false)
  // Abrir/baixar/pré-visualizar pelo backend — módulo comum (entrega B, 14/10/2026).
  const { abrirArquivo, preview, fecharPreview, ocupadoId: docAcaoId } = useArquivoReserva()

  const carregar = useCallback(async () => {
    if (!id) return
    setCarregando(true)
    setErro(null)
    const novosAvisos: string[] = []
    try {
      const [detalhe, fila] = await Promise.all([
        estruturacaoConsultorService.reserva(id),
        curadoriaService.fila().catch(() => null),
      ])
      setReserva(detalhe)
      setNaFila(fila?.reservas.find((r) => r.id === id) ?? null)

      const [ck, sols, docs, diag, resps, formCadastro, formEstruturacao] = await Promise.all([
        checklistReservaService.obter(id).catch(() => {
          novosAvisos.push('Checklist de documentos indisponível.')
          return null
        }),
        checklistReservaService.solicitacoes(id).catch(() => {
          novosAvisos.push('Solicitações da Curadoria indisponíveis.')
          return [] as SolicitacaoDocumentoReserva[]
        }),
        documentoService
          .listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, id, 0, 500)
          .then((p) => p.content ?? [])
          .catch(() => {
            novosAvisos.push('Lista de arquivos indisponível.')
            return [] as Documento[]
          }),
        diagnosticoService.obter(id).catch(() => {
          novosAvisos.push('Diagnóstico indisponível.')
          return null
        }),
        oportunidadeService.respostasElegibilidade(id).catch(() => {
          novosAvisos.push('Respostas de elegibilidade indisponíveis.')
          return [] as RespostaElegibilidadeReserva[]
        }),
        ...ETAPAS_FORMULARIO.map(({ etapa }) =>
          formularioService.obter(id, etapa).catch(() => {
            novosAvisos.push(
              `Formulário do produto (${etapa === 'CADASTRO' ? 'cadastro' : 'estruturação'}) indisponível.`
            )
            return null
          })
        ),
      ])
      setFormularios({ CADASTRO: formCadastro, ESTRUTURACAO: formEstruturacao })
      setChecklist(ck)
      setSolicitacoes(sols)
      setDocumentos(docs)
      setDiagnostico(diag)
      setRespostasElegibilidade(resps)
      if (detalhe.produtoId) {
        try {
          setCampos(await diagnosticoService.camposDoProduto(detalhe.produtoId))
        } catch {
          novosAvisos.push('Campos do diagnóstico do produto indisponíveis.')
        }
      } else {
        setCampos([])
      }
      setAvisos(novosAvisos)
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      setErro(msg || 'Não foi possível carregar a reserva.')
    } finally {
      setCarregando(false)
    }
  }, [id])

  useEffect(() => {
    if (backoffice) void carregar()
  }, [backoffice, carregar])

  /** Depois de validar/recusar/solicitar: relê só o checklist, as solicitações e os arquivos. */
  const recarregarDocumentos = useCallback(async () => {
    if (!id) return
    const [ck, sols, docs] = await Promise.all([
      checklistReservaService.obter(id).catch(() => null),
      checklistReservaService.solicitacoes(id).catch(() => null),
      documentoService
        .listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, id, 0, 500)
        .then((p) => p.content ?? [])
        .catch(() => null),
    ])
    if (ck) setChecklist(ck)
    if (sols) setSolicitacoes(sols)
    if (docs) setDocumentos(docs)
  }, [id])

  // Lista do que o backend marcou como não entregue (`presente`); contagens vêm prontas.
  const obrigatoriosPendentes = useMemo(
    () =>
      gruposComItens(checklist)
        .flatMap((g) => g.itens)
        .filter((i) => i.obrigatorio && !i.presente)
        .map(rotuloDoItem),
    [checklist]
  )
  const categoriasDaVersao = useMemo(
    () =>
      gruposComItens(checklist)
        .filter((g) => !!g.codigo && !g.itens.every((i) => i.origem === 'SOLICITACAO_CURADORIA'))
        .map((g) => ({ codigo: g.codigo as string, rotulo: rotuloDoGrupo(g) })),
    [checklist]
  )

  /**
   * "Mudar para análise": ENVIADA (Aguardando aprovação) → EM_ANALISE_ORIGINEI. É o time
   * assumindo a reserva — fica na atividade/auditoria com o autor do token. A decisão
   * final também aceita partir de ENVIADA (o backend transita antes), mas a tela pede o
   * passo explícito para que o consultor veja "Em análise" enquanto a conferência acontece.
   */
  const iniciarAnalise = async () => {
    if (!id || iniciando) return
    setIniciando(true)
    setErroDecisao(null)
    setBloqueio(null)
    try {
      await oportunidadeService.transicionarStatus(id, {
        novoStatus: 'EM_ANALISE_ORIGINEI',
        usuarioId: usuario?.id ?? '',
        observacao: 'Validação iniciada pelo time Originei',
      })
      enqueueSnackbar('Reserva em análise.', { variant: 'success' })
      await carregar()
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      const itens = itensBloqueantesDoErro(e)
      if (itens.length > 0) setBloqueio({ mensagem: msg || 'Documentos obrigatórios pendentes.', itens })
      else setErroDecisao(msg || 'Não foi possível colocar a reserva em análise.')
    } finally {
      setIniciando(false)
    }
  }

  /** Texto livre que ainda segura a aprovação: pendente ou "não atende" (09/09). */
  const textosSemAtende = respostasElegibilidade.filter(
    (r) => r.avaliacaoManual === 'PENDENTE' || r.avaliacaoManual === 'NAO_ATENDE'
  )

  const avaliarResposta = async (r: RespostaElegibilidadeReserva, resultado: AvaliacaoManual) => {
    if (!id) return
    setAvaliandoPergunta(r.perguntaId)
    try {
      const atualizada = await oportunidadeService.avaliarRespostaElegibilidade(id, r.perguntaId, { resultado })
      setRespostasElegibilidade((prev) => prev.map((x) => (x.perguntaId === r.perguntaId ? atualizada : x)))
      enqueueSnackbar(
        resultado === 'ATENDE' ? 'Resposta marcada como "atende".' : 'Resposta marcada como "não atende".',
        { variant: resultado === 'ATENDE' ? 'success' : 'warning' }
      )
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      enqueueSnackbar(msg || 'Não foi possível registrar a avaliação.', { variant: 'error' })
    } finally {
      setAvaliandoPergunta(null)
    }
  }

  const podeAprovar = dadosConferidos && docsConferidos && textosSemAtende.length === 0
  const podeDecidir =
    !decidindo &&
    motivo.trim().length > 0 &&
    (decisao !== 'APROVAR' || podeAprovar)

  const decidir = async () => {
    if (!id || !podeDecidir) return
    setDecidindo(true)
    setErroDecisao(null)
    setBloqueio(null)
    try {
      await curadoriaService.decidir(id, {
        decisao,
        motivo: motivo.trim(),
        pendencia:
          decisao === 'SOLICITAR_COMPLEMENTO'
            ? {
                descricao: pendDescricao.trim() || null,
                documentoSolicitado: pendDocumento.trim() || null,
                prazoResposta: pendPrazo || null,
              }
            : null,
      })
      enqueueSnackbar(
        decisao === 'APROVAR'
          ? 'Reserva aprovada — dados e documentos validados. O consultor já pode agendar a reunião.'
          : `${DECISAO_CURADORIA_LABEL[decisao]}: decisão registrada.`,
        { variant: 'success' }
      )
      navigate('/validacao-reservas')
    } catch (e) {
      const msg = (e as { message?: string } | null)?.message
      const itens = itensBloqueantesDoErro(e)
      if (itens.length > 0) setBloqueio({ mensagem: msg || 'O checklist impede esta decisão.', itens })
      else setErroDecisao(msg || 'Não foi possível registrar a decisão.')
    } finally {
      setDecidindo(false)
    }
  }

  if (!backoffice) return <Navigate to="/reservas" replace />
  if (!id) return <Navigate to="/validacao-reservas" replace />

  if (carregando) {
    return (
      <Box>
        <PageHeader title="Validação da reserva" />
        <EstadoDeLista estado="carregando" />
      </Box>
    )
  }
  if (erro || !reserva) {
    return (
      <Box>
        <PageHeader title="Validação da reserva" />
        <EstadoDeLista
          estado="erro"
          mensagem={erro ?? 'Reserva não encontrada'}
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      </Box>
    )
  }

  // Rascunho aparece na fila desde 09/09, mas não se valida nem se decide antes do envio.
  const aguardando = naFila != null && naFila.status !== 'RASCUNHO'
  const numero = reserva.numero != null ? `RES-${String(reserva.numero).padStart(4, '0')}` : null
  const notas = (reserva as { notas?: string | null }).notas ?? null

  return (
    <Box>
      <PageHeader
        title="Validação da reserva"
        subtitle={`${nomeEmpresa(reserva)}${numero ? ` · ${numero}` : ''} — confira os dados e os documentos inseridos pelo consultor antes de aprovar`}
        acao={<Button onClick={() => navigate('/validacao-reservas')}>Voltar à fila</Button>}
      />

      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 2 }}>
        {reserva.status && (
          <Chip size="small" variant="outlined" label={STATUS_LABEL[reserva.status] ?? reserva.status} />
        )}
        {naFila && (
          <Chip
            size="small"
            color={STATUS_CURADORIA_COR[naFila.statusCuradoria]}
            label={STATUS_CURADORIA_LABEL[naFila.statusCuradoria]}
          />
        )}
        {reserva.canal && <Chip size="small" variant="outlined" label={rotuloCanal(reserva.canal)} />}
      </Stack>

      {!aguardando && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Esta reserva não está mais na fila de validação: a decisão já foi registrada ou ela
          ainda não foi enviada pelo consultor. Os dados seguem disponíveis para consulta.
        </Alert>
      )}
      {avisos.map((a) => (
        <Alert key={a} severity="warning" sx={{ mb: 1 }}>
          {a}
        </Alert>
      ))}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 400px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Stack spacing={3}>
          {/* ── Dados da reserva ── */}
          <Paper sx={{ p: 3 }} component="section" aria-label="Dados da reserva">
            <Typography variant="h6" sx={{ mb: 2 }}>
              Dados da reserva
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <Campo rotulo="Razão social" valor={reserva.razaoSocial} />
              <Campo rotulo="Nome fantasia" valor={reserva.nomeFantasia} />
              <Campo rotulo="CNPJ" valor={reserva.cnpj ? formatCNPJ(reserva.cnpj) : null} />
              <Campo rotulo="Produto" valor={reserva.tipoNecessidade} />
              <Campo rotulo="Valor estimado" valor={formatCurrency(reserva.valorEstimado)} />
              <Campo rotulo="Canal" valor={rotuloCanal(reserva.canal)} />
              <Campo rotulo="Criada em" valor={formatDateTime(reserva.createdAt)} />
              <Campo rotulo="Validade da reserva" valor={reserva.prazoReserva ? formatDate(reserva.prazoReserva) : null} />
              {/* Nome gravado na reserva (V49); o id só em reserva antiga sem nome conhecido. */}
              <Campo rotulo="Consultor responsável" valor={reserva.consultorNome ?? reserva.usuarioResponsavel} />
              <Campo rotulo="Descrição / necessidade" valor={reserva.descricao} largo />
              <Campo rotulo="Notas do cadastro (faturamento, setor, origem dos dados)" valor={notas} largo />
            </Box>

            {/* L6: os dados que o produto pede vêm do formulário dinâmico, lido pela versão
                vinculada (a LEGADO traz os antigos dados complementares). Nada fixo. */}
            {ETAPAS_FORMULARIO.map(({ etapa, titulo }) => {
              const f = formularios[etapa]
              if (!mostrarFormulario(f) || !f.schema) return null
              const versao = rotuloVersao(f)
              return (
                <Box key={etapa} component="section" aria-label={titulo}>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2">{titulo}</Typography>
                    {versao && <Chip size="small" variant="outlined" label={versao} />}
                  </Stack>
                  <RespostasFormularioLeitura schema={f.schema} respostas={f.respostas ?? []} vazio={NAO_INFORMADO} />
                </Box>
              )
            })}
          </Paper>

          {/* P1 (AN-PP1 §7): "Outra necessidade" — o back-office reclassifica aqui, na
              validação; recarrega para checklist e formulário passarem a ser os do destino. */}
          <ReclassificacaoProdutoCard
            oportunidade={reserva}
            podeReclassificar={backoffice}
            onReclassificada={() => void carregar()}
          />

          {/* ── Diagnóstico ── */}
          <Paper sx={{ p: 3 }} component="section" aria-label="Diagnóstico do produto">
            <Typography variant="h6" sx={{ mb: 2 }}>
              Diagnóstico do produto
            </Typography>
            {campos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Este produto não tem campos de diagnóstico cadastrados.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2,
                }}
              >
                {campos.map((c) => (
                  <Campo
                    key={c.id}
                    rotulo={`${c.rotulo}${c.obrigatorio ? ' *' : ''}`}
                    valor={valorDaResposta(c, diagnostico)}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {/* ── Documentos ── */}
          <Paper sx={{ p: 3 }} component="section" aria-label="Documentos da reserva">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Typography variant="h6">Documentos</Typography>
              {checklist && (
                <Chip
                  size="small"
                  color={obrigatoriosPendentes.length === 0 ? 'success' : 'warning'}
                  label={
                    checklist.total === 0
                      ? 'Sem documentos exigidos'
                      : `${checklist.entregues ?? checklist.presentes} de ${checklist.total} entregues` +
                        (checklist.validados != null ? ` · ${checklist.validados} validados` : '')
                  }
                />
              )}
              <Box sx={{ flex: 1 }} />
              {checklist?.versao && !checklist.versao.ehVigente && (
                <Button size="small" onClick={() => setAtualizarVersaoAberto(true)}>
                  Atualizar checklist para a versão vigente
                </Button>
              )}
              {reserva.status !== 'RASCUNHO' && (
                <Button size="small" variant="outlined" onClick={() => setSolicitarAberto(true)}>
                  Solicitar documento
                </Button>
              )}
            </Stack>
            <CabecalhoVersaoChecklist versao={checklist?.versao} />

            {/* Checklist com os arquivos DENTRO de cada item; Validar/Recusar por item (P3). */}
            <ChecklistComArquivos
              checklist={checklist}
              documentos={documentos}
              ocupadoId={docAcaoId}
              onAbrir={(d, download) => void abrirArquivo(d, download)}
              mostrarAutor
              acoes={{
                onValidar: setValidando,
                onRecusar: setRecusando,
                // Na curadoria, cada item tem "Devolver" (vira pendência); depois dela, o Recusar simples.
                onDevolver: STATUS_DEVOLVER.has(reserva.status ?? '') ? setDevolvendo : undefined,
                onHistorico: setHistorico,
                habilitadas: reserva.status !== 'RASCUNHO',
              }}
            />

            {solicitacoes.length > 0 && (
              <Box component="section" aria-label="Solicitações da Curadoria" sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Solicitações da Curadoria
                </Typography>
                <Stack spacing={1}>
                  {solicitacoes.map((s) => (
                    <Paper key={s.chave} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.rotulo}
                        </Typography>
                        <Chip size="small" variant="outlined" label={s.chave} />
                        {s.obrigatorio && <Chip size="small" color="warning" variant="outlined" label="Obrigatório" />}
                        {s.canceladaEm ? (
                          <Chip size="small" label={`Cancelada em ${formatDateTime(s.canceladaEm)}`} />
                        ) : (
                          <Button
                            size="small"
                            color="error"
                            sx={{ ml: 'auto' }}
                            onClick={() => setCancelando(s)}
                            aria-label={`Cancelar solicitação ${s.rotulo}`}
                          >
                            Cancelar
                          </Button>
                        )}
                      </Stack>
                      {s.motivo && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          Motivo: {s.motivo}
                          {s.solicitadoEm ? ` · solicitado em ${formatDateTime(s.solicitadoEm)}` : ''}
                        </Typography>
                      )}
                      {s.motivoCancelamento && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          Cancelamento: {s.motivoCancelamento}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        </Stack>

        {/* ── Respostas de elegibilidade (09/09) ── */}
        <Paper sx={{ p: 3 }} component="section" aria-label="Respostas de elegibilidade">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="h6">Respostas de elegibilidade</Typography>
            {textosSemAtende.length > 0 && (
              <Chip size="small" color="warning" label={`${textosSemAtende.length} para avaliar`} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            O que o consultor respondeu ao criar a reserva. Sim/Não, Número e Escolha já foram
            decididos pelo motor; <strong>texto livre é decidido aqui</strong> — a aprovação só é
            liberada com todas as respostas em texto marcadas como "Atende".
          </Typography>
          {respostasElegibilidade.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Este produto não tinha perguntas de elegibilidade respondidas.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {respostasElegibilidade.map((r) => {
                const texto = r.tipoResposta === 'TEXTO'
                const valor =
                  r.tipoResposta === 'SIM_NAO'
                    ? r.resposta === 'true' ? 'Sim' : r.resposta === 'false' ? 'Não' : r.resposta
                    : r.resposta
                return (
                  <Paper key={r.perguntaId} variant="outlined" sx={{ p: 2 }} component="article" aria-label={r.enunciado}>
                    {/* Sempre empilhado: em tela larga este card fica na coluna lateral de 400px, e
                        pergunta + chip + dois botões lado a lado espremiam o texto e estouravam o
                        card (Jornada ADM 30/10, item 2). */}
                    <Stack spacing={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {r.enunciado}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                          {valor == null || valor === '' ? '—' : valor}
                        </Typography>
                        {r.avaliacaoObservacao && (
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            Observação: {r.avaliacaoObservacao}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={AVALIACAO_MANUAL_LABEL[r.avaliacaoManual]}
                          color={
                            r.avaliacaoManual === 'ATENDE'
                              ? 'success'
                              : r.avaliacaoManual === 'NAO_ATENDE'
                                ? 'error'
                                : r.avaliacaoManual === 'PENDENTE'
                                  ? 'warning'
                                  : 'default'
                          }
                        />
                        {texto && r.resposta && (
                          <>
                            <Button
                              size="small"
                              variant={r.avaliacaoManual === 'ATENDE' ? 'contained' : 'outlined'}
                              color="success"
                              disabled={!aguardando || avaliandoPergunta === r.perguntaId || r.avaliacaoManual === 'ATENDE'}
                              onClick={() => void avaliarResposta(r, 'ATENDE')}
                              aria-label={`Atende: ${r.enunciado}`}
                            >
                              Atende
                            </Button>
                            <Button
                              size="small"
                              variant={r.avaliacaoManual === 'NAO_ATENDE' ? 'contained' : 'outlined'}
                              color="error"
                              disabled={!aguardando || avaliandoPergunta === r.perguntaId || r.avaliacaoManual === 'NAO_ATENDE'}
                              onClick={() => void avaliarResposta(r, 'NAO_ATENDE')}
                              aria-label={`Não atende: ${r.enunciado}`}
                            >
                              Não atende
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                )
              })}
            </Stack>
          )}
        </Paper>

        {/* ── Aprovação ── */}
        <Paper sx={{ p: 3, position: { lg: 'sticky' }, top: { lg: 16 } }} component="section" aria-label="Aprovação">
          <Typography variant="h6" sx={{ mb: 1 }}>
            Aprovação
          </Typography>
          {naFila && (naFila.conflitosCnpj.length > 0 || naFila.conflitosForaDoEscopo > 0) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Duplicidade possível: {naFila.conflitosCnpj.length + naFila.conflitosForaDoEscopo}{' '}
              outra(s) reserva(s) com este CNPJ. Confira na fila antes de aprovar.
            </Alert>
          )}
          {obrigatoriosPendentes.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Obrigatórios pendentes: {obrigatoriosPendentes.join(', ')}.
            </Alert>
          )}
          {textosSemAtende.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Respostas em texto livre sem "Atende":{' '}
              {textosSemAtende.map((r) => `${r.enunciado} (${AVALIACAO_MANUAL_LABEL[r.avaliacaoManual].toLowerCase()})`).join('; ')}.
              Avalie na seção "Respostas de elegibilidade" para liberar a aprovação.
            </Alert>
          )}

          {aguardando && reserva.status === 'ENVIADA' && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void iniciarAnalise()}
                  disabled={iniciando}
                >
                  {iniciando ? 'Iniciando…' : 'Iniciar análise'}
                </Button>
              }
            >
              Reserva aguardando aprovação. Inicie a análise para assumi-la — o consultor
              passa a ver "Em análise".
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={dadosConferidos}
                onChange={(e) => setDadosConferidos(e.target.checked)}
                disabled={!aguardando}
              />
            }
            label="Dados da reserva conferidos"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={docsConferidos}
                onChange={(e) => setDocsConferidos(e.target.checked)}
                disabled={!aguardando}
              />
            }
            label="Documentos conferidos"
          />

          <Divider sx={{ my: 2 }} />

          <FormControl component="fieldset" sx={{ display: 'block' }} disabled={!aguardando}>
            <RadioGroup
              aria-label="Decisão da validação"
              value={decisao}
              onChange={(e) => setDecisao(e.target.value as DecisaoCuradoria)}
            >
              <FormControlLabel value="APROVAR" control={<Radio size="small" />} label="Aprovar reserva" />
              <FormControlLabel
                value="SOLICITAR_COMPLEMENTO"
                control={<Radio size="small" />}
                label={DECISAO_CURADORIA_LABEL.SOLICITAR_COMPLEMENTO}
              />
              <FormControlLabel value="REPROVAR" control={<Radio size="small" />} label={DECISAO_CURADORIA_LABEL.REPROVAR} />
            </RadioGroup>
          </FormControl>
          <TextField
            label={decisao === 'APROVAR' ? 'Observação da validação' : 'Motivo'}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            disabled={!aguardando}
            helperText="Obrigatório em qualquer decisão — fica na atividade da reserva."
          />
          {decisao === 'SOLICITAR_COMPLEMENTO' && (
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <TextField label="O que falta" value={pendDescricao} onChange={(e) => setPendDescricao(e.target.value)} fullWidth />
              <TextField label="Documento solicitado" value={pendDocumento} onChange={(e) => setPendDocumento(e.target.value)} fullWidth />
              <TextField
                label="Prazo de resposta"
                type="date"
                value={pendPrazo}
                onChange={(e) => setPendPrazo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          )}
          {decisao === 'APROVAR' && !podeAprovar && aguardando && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Marque as duas conferências para habilitar a aprovação.
            </Typography>
          )}
          {erroDecisao && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erroDecisao}
            </Alert>
          )}
          {bloqueio && <AlertaItensBloqueantes mensagem={bloqueio.mensagem} itens={bloqueio.itens} />}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color={decisao === 'REPROVAR' ? 'error' : 'primary'}
              onClick={() => void decidir()}
              disabled={!aguardando || !podeDecidir}
            >
              {decidindo
                ? 'Registrando…'
                : decisao === 'APROVAR'
                  ? 'Aprovar reserva'
                  : DECISAO_CURADORIA_LABEL[decisao]}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Aprovada, a reserva passa a "Aprovada Originei" e o consultor pode agendar a reunião.
          </Typography>
        </Paper>
      </Box>

      {/* P3 — validação documental por item */}
      <ValidarItemDialog
        open={validando != null}
        reservaId={id}
        item={validando}
        onFechar={() => setValidando(null)}
        onValidado={(d) => {
          setValidando(null)
          enqueueSnackbar(
            d.validoAte ? `Documento validado — válido até ${formatDate(d.validoAte)}.` : 'Documento validado.',
            { variant: 'success' }
          )
          void recarregarDocumentos()
        }}
      />
      <RecusarItemDialog
        open={recusando != null}
        reservaId={id}
        item={recusando}
        onFechar={() => setRecusando(null)}
        onRecusado={() => {
          setRecusando(null)
          enqueueSnackbar('Documento recusado — o consultor verá o motivo na Central de Pendências.', {
            variant: 'success',
          })
          void recarregarDocumentos()
        }}
      />
      <DevolverItemDialog
        open={devolvendo != null}
        reservaId={id}
        item={devolvendo}
        onFechar={() => setDevolvendo(null)}
        onDevolvido={() => {
          setDevolvendo(null)
          enqueueSnackbar('Documento devolvido — a reserva está em pendência e o consultor foi avisado.', {
            variant: 'success',
          })
          void carregar()
        }}
      />
      <HistoricoDecisoesDialog
        open={historico != null}
        reservaId={id}
        item={historico}
        mostrarAutor
        onFechar={() => setHistorico(null)}
      />
      <SolicitarDocumentoDialog
        open={solicitarAberto}
        reservaId={id}
        categorias={categoriasDaVersao}
        onFechar={() => setSolicitarAberto(false)}
        onSolicitado={(s) => {
          setSolicitarAberto(false)
          enqueueSnackbar(`Documento solicitado (${s.chave}).`, { variant: 'success' })
          void recarregarDocumentos()
        }}
      />
      <MotivoDialog
        open={cancelando != null}
        titulo={cancelando ? `Cancelar solicitação: ${cancelando.rotulo}` : 'Cancelar solicitação'}
        descricao="Os arquivos já enviados para esta solicitação continuam na reserva."
        confirmar="Cancelar solicitação"
        mensagemErro={(e) => mensagemErroChecklistReserva(e, 'Não foi possível cancelar a solicitação.')}
        onFechar={() => setCancelando(null)}
        onConfirmar={async (motivoCancelamento) => {
          if (!cancelando) return
          await checklistReservaService.cancelarSolicitacao(id, cancelando.chave, motivoCancelamento)
          setCancelando(null)
          enqueueSnackbar('Solicitação cancelada.', { variant: 'success' })
          void recarregarDocumentos()
        }}
      />
      <MotivoDialog
        open={atualizarVersaoAberto}
        titulo="Atualizar checklist para a versão vigente"
        descricao={
          checklist?.versao
            ? `A reserva segue a versão ${checklist.versao.numero}. Os arquivos acompanham pela chave do item; decisões só continuam valendo onde a definição do item não mudou.`
            : undefined
        }
        confirmar="Atualizar"
        mensagemErro={(e) => mensagemErroChecklistReserva(e, 'Não foi possível atualizar a versão do checklist.')}
        onFechar={() => setAtualizarVersaoAberto(false)}
        onConfirmar={async (motivoAtualizacao) => {
          const r = await checklistReservaService.atualizarVersao(id, motivoAtualizacao)
          setAtualizarVersaoAberto(false)
          enqueueSnackbar(
            r.numero != null ? `Checklist atualizado para a versão ${r.numero}.` : 'Checklist atualizado.',
            { variant: 'success' }
          )
          void recarregarDocumentos()
        }}
      />

      {/* Visualização do arquivo (PDF/imagem) sem sair da tela */}
      <ArquivoPreviewDialog
        preview={preview}
        onFechar={fecharPreview}
        onBaixar={(d) => void abrirArquivo(d, true)}
      />
    </Box>
  )
}

export default ValidacaoReservaPage
